/**
 * useMoveLead — optimistic stage move. `onMutate` moves the card in every
 * cached leads list (and sets `stage_entered_at` so the "days in stage" chip
 * resets), `onError` restores the snapshots, `onSettled` refetches.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useApiClient } from '@/lib/api-client';
import { LEADS_KEY, leadKey, patchLead } from './api';
import type { LeadCard, LeadDetail } from './types';

export interface MoveLeadInput {
  leadId: string;
  toStageId: string;
}

type Snapshot = Array<[readonly unknown[], LeadCard[] | undefined]>;

export function useMoveLead() {
  const client = useApiClient();
  const qc = useQueryClient();

  return useMutation({
    mutationFn: ({ leadId, toStageId }: MoveLeadInput) =>
      patchLead(client, leadId, { stage_id: toStageId }),

    onMutate: async ({ leadId, toStageId }) => {
      await qc.cancelQueries({ queryKey: LEADS_KEY });
      const now = new Date().toISOString();
      const lists = qc.getQueriesData<LeadCard[]>({ queryKey: LEADS_KEY });
      const snapshot: Snapshot = lists.map(([key, data]) => [key, data]);

      for (const [key, data] of lists) {
        if (!data) continue;
        qc.setQueryData<LeadCard[]>(
          key,
          data.map((card) =>
            card.id === leadId ? { ...card, stage_id: toStageId, stage_entered_at: now } : card,
          ),
        );
      }
      const detailKey = leadKey(leadId);
      const detail = qc.getQueryData<LeadDetail>(detailKey);
      if (detail) {
        qc.setQueryData<LeadDetail>(detailKey, { ...detail, stage_id: toStageId, stage_entered_at: now });
      }
      return { snapshot, detail };
    },

    onError: (_err, { leadId }, ctx) => {
      if (!ctx) return;
      for (const [key, data] of ctx.snapshot) qc.setQueryData(key, data);
      if (ctx.detail) qc.setQueryData(leadKey(leadId), ctx.detail);
    },

    onSettled: (_data, _err, { leadId }) => {
      void qc.invalidateQueries({ queryKey: LEADS_KEY });
      void qc.invalidateQueries({ queryKey: leadKey(leadId) });
    },
  });
}
